import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, RefreshCw, Search } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";

type Status = "new" | "reviewed" | "contacted" | "closed";
const STATUSES: Status[] = ["new", "reviewed", "contacted", "closed"];

interface Submission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  interest: string;
  budget: string | null;
  preferred_location: string | null;
  message: string;
  source: string | null;
  status: Status;
  admin_notes: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string | null;
}

const STATUS_STYLE: Record<Status, string> = {
  new: "bg-accent/15 text-accent border-accent/30",
  reviewed: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  contacted: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
};

const StatusBadge = ({ status }: { status: Status }) => (
  <span
    className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${STATUS_STYLE[status]}`}
  >
    {status}
  </span>
);

const csvEscape = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const AdminSubmissions = () => {
  const [rows, setRows] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Status>("all");
  const [interestFilter, setInterestFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Submission | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  useEffect(() => {
    document.title = "Submissions | Hadassah Homes Admin";
    void loadRows();
  }, []);

  const loadRows = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setRows((data ?? []) as Submission[]);
    setLoading(false);
  };

  const interests = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.interest && s.add(r.interest));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (interestFilter !== "all" && r.interest !== interestFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        (r.phone ?? "").toLowerCase().includes(q) ||
        r.message.toLowerCase().includes(q) ||
        (r.preferred_location ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter, interestFilter]);

  const openDetail = (row: Submission) => {
    setSelected(row);
    setNotesDraft(row.admin_notes ?? "");
  };

  const updateRow = async (id: string, patch: Partial<Submission>) => {
    const { data, error: err } = await supabase
      .from("contact_submissions")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (err) {
      toast.error(err.message);
      return null;
    }
    const updated = data as Submission;
    setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
    if (selected?.id === id) setSelected(updated);
    return updated;
  };

  const handleStatusChange = async (id: string, status: Status) => {
    const patch: Partial<Submission> = { status };
    if (status !== "new" && !rows.find((r) => r.id === id)?.reviewed_at) {
      patch.reviewed_at = new Date().toISOString();
      const { data: u } = await supabase.auth.getUser();
      if (u.user) patch.reviewed_by = u.user.id;
    }
    const result = await updateRow(id, patch);
    if (result) toast.success(`Marked as ${status}`);
  };

  const handleMarkReviewed = async () => {
    if (!selected) return;
    const { data: u } = await supabase.auth.getUser();
    const result = await updateRow(selected.id, {
      status: "reviewed",
      reviewed_at: new Date().toISOString(),
      reviewed_by: u.user?.id ?? null,
    });
    if (result) toast.success("Marked as reviewed");
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    const result = await updateRow(selected.id, { admin_notes: notesDraft });
    setSavingNotes(false);
    if (result) toast.success("Notes saved");
  };

  const exportCsv = () => {
    const headers = [
      "created_at",
      "name",
      "email",
      "phone",
      "interest",
      "budget",
      "preferred_location",
      "source",
      "status",
      "message",
      "admin_notes",
      "reviewed_at",
    ];
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      lines.push(headers.map((h) => csvEscape((r as any)[h])).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout
      title="Submissions"
      description="Review contact and consultation requests from the public website. Updating status here is for internal tracking only."
    >
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
            <div className="relative md:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, message…"
                className="rounded-none pl-9"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | Status)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={interestFilter}
              onChange={(e) => setInterestFilter(e.target.value)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All interests</option>
              {interests.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void loadRows()}
              disabled={loading}
              className="rounded-none"
              title="Refresh"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={filtered.length === 0}
              className="rounded-none"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">
              No submissions match your filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Phone</th>
                    <th className="px-4 py-3">Interest</th>
                    <th className="px-4 py-3 hidden xl:table-cell">Source</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 hidden md:table-cell">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => openDetail(r)}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{r.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                        {r.phone ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{r.interest}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden xl:table-cell">
                        {r.source ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="max-w-md truncate px-4 py-3 text-muted-foreground hidden md:table-cell">
                        {r.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">{filtered.length} of {rows.length} submissions</p>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">{selected.name}</SheetTitle>
                <SheetDescription>
                  Submitted {new Date(selected.created_at).toLocaleString()}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="flex items-center gap-3">
                  <StatusBadge status={selected.status} />
                  <select
                    value={selected.status}
                    onChange={(e) => handleStatusChange(selected.id, e.target.value as Status)}
                    className="h-9 rounded-none border border-input bg-background px-3 text-sm"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        Set status: {s}
                      </option>
                    ))}
                  </select>
                </div>

                <dl className="space-y-3 text-sm">
                  <Field label="Email" value={selected.email} />
                  <Field label="Phone" value={selected.phone} />
                  <Field label="Interest" value={selected.interest} />
                  <Field label="Budget" value={selected.budget} />
                  <Field label="Preferred location" value={selected.preferred_location} />
                  <Field label="Source" value={selected.source} />
                  {selected.reviewed_at ? (
                    <Field
                      label="Reviewed at"
                      value={new Date(selected.reviewed_at).toLocaleString()}
                    />
                  ) : null}
                </dl>

                <div>
                  <p className="mb-2 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                    Message
                  </p>
                  <p className="whitespace-pre-wrap border border-border bg-muted/30 p-4 text-sm leading-relaxed">
                    {selected.message}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                    Internal notes
                  </p>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={5}
                    placeholder="Notes visible only to the admin team…"
                    className="rounded-none"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                      variant="gold"
                      className="rounded-none"
                    >
                      {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save notes"}
                    </Button>
                    {selected.status === "new" ? (
                      <Button variant="outline" onClick={handleMarkReviewed} className="rounded-none">
                        Mark as reviewed
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
};

const Field = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3">
    <dt className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</dt>
    <dd className="text-foreground">{value || "—"}</dd>
  </div>
);

export default AdminSubmissions;
